import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../components/Layout";
import PostingForm from "../components/AccuratePosting/PostingForm";
import SerialRowTable from "../components/AccuratePosting/SerialRowTable";
import {
  getAccurateModules,
  getAccurateTransaction,
  getSerialNumberMutations,
  submitAccurateTransaction,
} from "../services/api";
import type {
  AccurateModuleDefinition,
  AccuratePostingTransaction,
} from "../types/accuratePosting";
import { validateAccuratePostingTransaction } from "../utils/accuratePostingValidation";
import { normalizeAccuratePostingTransaction } from "../utils/normalizeAccuratePostingTransaction";
import {
  buildSerialFormatPreview,
  getSerialQueryRange,
  getSerialSequenceKey,
  getNextSerialIncrement,
} from "../utils/accuratePostingFormat";

const SERIAL_FORMAT_OPTIONS = [
  ["[MONTH]", "Bulan"],
  ["[YEAR]", "Tahun"],
  ["[DATE]", "Tanggal"],
  ["[INCREMENT]", "Increment"],
  ["[UUID]", "UUID"],
  ["[RANDOM_NUMBER]", "Nomor Acak"],
  ["[CODE]", "Kode Barang"],
  ["[OTHER]", "Lainnya"],
] as const;

const AccuratePosting: React.FC = () => {
  const [modules, setModules] = useState<AccurateModuleDefinition[]>([]);
  const [moduleType, setModuleType] = useState("");
  const [number, setNumber] = useState("");
  const [transaction, setTransaction] = useState<AccuratePostingTransaction | null>(null);
  const [errors, setErrors] = useState<ReturnType<typeof validateAccuratePostingTransaction>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingSerials, setIsGeneratingSerials] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formatComponents, setFormatComponents] = useState<string[]>([]);
  const [selectedFormatComponent, setSelectedFormatComponent] = useState("");
  const [otherFormatComponent, setOtherFormatComponent] = useState("");
  const [formatDigitCount, setFormatDigitCount] = useState(5);
  const nextIncrementBySequence = useRef(new Map<string, number>());
  const hasIncrement = formatComponents.includes("[INCREMENT]");
  const canAddIncrement =
    formatComponents.length > 0 &&
    formatComponents.every((component) => component !== "[INCREMENT]") &&
    formatComponents.every((component) =>
      component !== "[UUID]" && component !== "[RANDOM_NUMBER]",
    );

  useEffect(() => {
    let active = true;
    getAccurateModules()
      .then((response) => {
        if (active) {
          const availableModules = Array.isArray(response.data) ? response.data : [];
          setModules(availableModules);
          setModuleType(availableModules[0]?.moduleType || "");
        }
      })
      .catch((error: unknown) => {
        if (active) toast.error(error instanceof Error ? error.message : "Failed to fetch modules");
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setErrors(transaction ? validateAccuratePostingTransaction(transaction) : []);
  }, [transaction]);

  const handleLookup = async () => {
    if (!moduleType || !number.trim()) return;
    setIsLoading(true);
    setTransaction(null);
    nextIncrementBySequence.current.clear();
    setSubmitAttempted(false);
    try {
      const response = await getAccurateTransaction(moduleType, number.trim());
      if (!response?.data || !Array.isArray(response.data.detailItem)) {
        throw new Error("Invalid transaction response");
      }

      const transactionData = normalizeAccuratePostingTransaction(
        response.data,
        moduleType,
      );
      if (!transactionData.detailItem.some((detailItem) => detailItem.manageSN !== false)) {
        throw new Error("Transaksi tidak memiliki item yang mengelola serial number");
      }

      setTransaction(transactionData);
      setNumber(transactionData.number || number.trim());
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to fetch accurate transaction");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!transaction || errors.length > 0) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ...transaction,
        detailItem: transaction.detailItem
          .filter((detailItem) => detailItem.manageSN !== false)
          .map((detailItem) => {
            const detailPayload = {
              ...detailItem,
              detailSerialNumber: detailItem.detailSerialNumber.map(
                ({ isExisting: _isExisting, ...serialRow }) => serialRow,
              ),
            };
            if (transaction.moduleType === "RO") {
              const detailFields = detailPayload as Record<string, unknown>;
              delete detailFields.portion;
              delete detailFields.allocationAmount;
            }
            return detailPayload;
          }),
      };
      await submitAccurateTransaction(
        payload as Parameters<typeof submitAccurateTransaction>[0],
      );
      toast.success("Transaction submitted successfully");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to submit accurate transaction");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddFormatComponent = () => {
    const component =
      selectedFormatComponent === "[OTHER]"
        ? otherFormatComponent.trim()
        : selectedFormatComponent;

    if (!component) return;
    if (hasIncrement || (component === "[INCREMENT]" && !canAddIncrement)) return;
    setFormatComponents((current) => [...current, component]);
    setSelectedFormatComponent("");
    setOtherFormatComponent("");
  };

  const handleGenerateSerials = async (targets: { detailIndex: number; rowIndex?: number }[]) => {
    if (!transaction || targets.length === 0) return;
    const serialTargets = targets.filter(
      ({ detailIndex }) => transaction.detailItem[detailIndex]?.manageSN !== false,
    );
    if (serialTargets.length === 0) return;
    const hasUniqueWithoutIncrement = serialTargets.some(({ detailIndex }) =>
      transaction.detailItem[detailIndex].serialNumberType === "UNIQUE" &&
      !formatComponents.includes("[INCREMENT]"),
    );
    if (hasUniqueWithoutIncrement) {
      toast.error("Format UNIQUE wajib memiliki komponen [INCREMENT]");
      return;
    }
    setIsGeneratingSerials(true);
    try {
      const generatedItems = await Promise.all(serialTargets.map(async ({ detailIndex, rowIndex }) => {
        const detailItem = transaction.detailItem[detailIndex];
        const components = formatComponents;
        if (components.length === 0) throw new Error(`Format item ${detailItem.itemNo || detailIndex + 1} belum diisi`);
        const hasDateComponent = components.some((component) =>
          ["[DATE]", "[YEAR]", "[MONTH]"].includes(component),
        );
        if (hasDateComponent && !transaction.transDate) {
          throw new Error("Tanggal transaksi wajib diisi untuk format bertanggal");
        }
        const itemNo = detailItem.itemNo?.trim();
        if (!itemNo) throw new Error("Item number wajib diisi untuk generate serial");

        const sequenceKey = getSerialSequenceKey(
          components,
          { itemNo: detailItem.itemNo, rowCount: 1 },
          { digitCount: formatDigitCount, transDate: transaction.transDate },
        );
        let incrementStart: number | undefined;
        if (components.includes("[INCREMENT]")) {
          const localSerials = detailItem.detailSerialNumber.map((serial) => serial.serialNumberNo);
          const cachedNextIncrement = nextIncrementBySequence.current.get(sequenceKey);
          if (cachedNextIncrement !== undefined) {
            incrementStart = Math.max(
              cachedNextIncrement,
              getNextSerialIncrement(localSerials, sequenceKey),
            );
          } else {
            const range = getSerialQueryRange(components, transaction.transDate);
            const result = await getSerialNumberMutations(itemNo, range.fromDate, range.toDate);
            incrementStart = getNextSerialIncrement(
              [
                ...result.data.map((mutation) => mutation.serialNumber),
                ...localSerials,
              ],
              sequenceKey,
            );
          }
        }
        let generatedCount = 0;
        const nextSerials = detailItem.detailSerialNumber.map((serial, serialIndex) => {
          if (serial.serialNumberNo.trim()) return serial;
          if (rowIndex !== undefined && serialIndex !== rowIndex) return serial;
          const serialNumberNo = buildSerialFormatPreview(
            components,
            { itemNo: detailItem.itemNo, rowCount: 1 },
            {
              digitCount: formatDigitCount,
              transDate: transaction.transDate,
              index: generatedCount++,
              incrementStart,
            },
          );
          return { ...serial, serialNumberNo };
        });
        if (incrementStart !== undefined) {
          nextIncrementBySequence.current.set(sequenceKey, incrementStart + generatedCount);
        }
        return [detailIndex, { ...detailItem, detailSerialNumber: nextSerials }] as const;
      }));

      const generatedByIndex = new Map(generatedItems);
      setTransaction((current) => current ? {
        ...current,
        detailItem: current.detailItem.map((detailItem, detailIndex) =>
          generatedByIndex.get(detailIndex) || detailItem,
        ),
      } : current);
      toast.success("Serial number berhasil digenerate");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to generate serial number");
    } finally {
      setIsGeneratingSerials(false);
    }
  };

  const previewItem = transaction?.detailItem[0];
  const formatPreview = buildSerialFormatPreview(
    formatComponents,
    { itemNo: previewItem?.itemNo, rowCount: 1 },
    { digitCount: formatDigitCount, transDate: transaction?.transDate },
  );

  return (
    <Layout title="Posting Accurate" breadcrumbTitle="Posting Accurate">
      <div className="grid w-full gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(20rem,0.8fr)]">
        <section className="max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg bg-white p-4 shadow-md md:p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="mb-2 text-2xl font-bold">Posting Accurate</h1>
              <p className="text-sm text-gray-500">
                Direct-post serial number ke Accurate. Tidak ada serial history lokal.
              </p>
            </div>
            <div className="flex max-w-md shrink-0 items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white"
              >
                i
              </span>
              <div className="text-xs leading-5">
                <p className="font-semibold">Perhatian</p>
                {moduleType === "RO" && (
                  <p>
                    Modul Penyelesaian Pesanan hanya dapat digunakan untuk Tipe Penyelesaian Barang/Item.
                  </p>
                )}
                {moduleType !== "RO" && (
                  <p>
                    Modul ini hanya bisa digunakan ketika sub module Pengisian Nomor Seri di aktifkan
                  </p>
                )}
              </div>
            </div>
          </div>
          <PostingForm
            modules={modules}
            moduleType={moduleType}
            number={number}
            isLoading={isLoading}
            onModuleChange={setModuleType}
            onNumberChange={setNumber}
            onLookup={handleLookup}
          />
          {transaction && (
            <div className="mt-6 space-y-4">
              {transaction.moduleType === "FGS" && (
                <label className="flex max-w-xl flex-col gap-1 text-sm font-medium text-gray-700">
                  Job order number
                  <input
                    aria-label="Job order number"
                    className="rounded border border-gray-300 bg-gray-100 p-2 font-normal"
                    value={transaction.jobOrderNumber || ""}
                    readOnly
                  />
                </label>
              )}
              <SerialRowTable
                detailItems={transaction.detailItem.filter(
                  (detailItem) => detailItem.manageSN !== false,
                )}
                onDetailItemsChange={(detailItem) =>
                  setTransaction((current) =>
                    current ? { ...current, detailItem } : current,
                  )
                }
                onGenerateItem={(detailIndex) => handleGenerateSerials([{ detailIndex }])}
                onGenerateRow={(detailIndex, rowIndex) =>
                  handleGenerateSerials([{ detailIndex, rowIndex }])
                }
                isGenerating={isGeneratingSerials}
              />
              {submitAttempted && errors.length > 0 && (
                <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <ul>
                    {errors.map((error) => <li key={`${error.field}-${error.message}`}>{error.message}</li>)}
                  </ul>
                </div>
              )}
              <button
                type="button"
                className="rounded bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}
        </section>

        <aside className="self-start space-y-4 rounded-lg bg-white p-4 shadow-md md:p-6 xl:sticky xl:top-4">
          <div>
            <h2 className="text-xl font-bold">Compose Serial Template</h2>
            <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm leading-5 text-amber-900">
              <p>
                Perhatian: komponen [INCREMENT] akan melanjutkan nomor dari increment terakhir yang
                tercatat di Accurate. Pastikan format serial dan tanggal transaksi sesuai agar nomor
                yang dihasilkan tetap berurutan dan tidak bentrok.
              </p>
            </div>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Komponen Penomoran
               <select
                aria-label="Serial format component"
                className="rounded border border-gray-300 bg-white p-2 font-normal"
                value={selectedFormatComponent}
                disabled={hasIncrement}
                onChange={(event) => setSelectedFormatComponent(event.target.value)}
              >
                <option value="">Select component</option>
                {SERIAL_FORMAT_OPTIONS
                  .filter(([value]) => value !== "[INCREMENT]" || canAddIncrement || hasIncrement)
                  .map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
              </select>
            </div>
            {selectedFormatComponent === "[RANDOM_NUMBER]" && (
              <label className="mt-3 flex flex-col gap-1 text-sm font-medium text-gray-700">
                Jumlah Digit
                <input
                  aria-label="Format digit count"
                  className="rounded border border-gray-300 p-2 font-normal"
                  type="number"
                  min="1"
                  value={formatDigitCount}
                  onChange={(event) => setFormatDigitCount(Number(event.target.value) || 1)}
                />
              </label>
            )}
            {selectedFormatComponent === "[OTHER]" && (
              <label className="mt-3 flex flex-col gap-1 text-sm font-medium text-gray-700">
                Komponen Lainnya
                <input
                  aria-label="Other format component"
                  className="rounded border border-gray-300 p-2 font-normal"
                  value={otherFormatComponent}
                  onChange={(event) => setOtherFormatComponent(event.target.value)}
                  placeholder="Masukkan komponen lainnya"
                />
              </label>
            )}
            <button
              type="button"
              className="mt-3 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              disabled={hasIncrement}
              onClick={handleAddFormatComponent}
            >
              Add component
            </button>
            <div className="mt-3 flex flex-wrap gap-2">
               {formatComponents.length === 0 && (
                 <span className="text-sm text-gray-500">Belum ada komponen.</span>
               )}
               {formatComponents.map((component, index) => (
                <span key={`${component}-${index}`} className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                  {component}
                   <button
                     type="button"
                     aria-label={`Remove component ${index + 1}`}
                     onClick={() => setFormatComponents((current) =>
                       current.filter((_, itemIndex) => itemIndex !== index))}
                   >
                    x
                  </button>
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-blue-50 p-4 text-sm text-blue-900">
            <div className="font-semibold">Preview serial final</div>
            <div className="mt-2 break-all font-mono font-bold">{formatPreview || "-"}</div>
          </div>
          <div className="rounded-lg border p-4">
            <h3 className="font-semibold">Generate Action</h3>
              <button
                type="button"
                className="mt-3 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                disabled={isGeneratingSerials || !transaction}
                onClick={() => handleGenerateSerials(
                  transaction?.detailItem
                    .map((detailItem, detailIndex) => ({ detailItem, detailIndex }))
                    .filter(({ detailItem }) => detailItem.manageSN !== false)
                    .map(({ detailIndex }) => ({ detailIndex })) || [],
                )}
              >
               {isGeneratingSerials ? "Generating..." : "Generate All"}
               </button>
          </div>
        </aside>
      </div>
    </Layout>
  );
};

export default AccuratePosting;
